import { iatan2, idiv, imax, imin, isqrt, withinRange } from '../math/fixed.ts';
import type { BattleState, Hero } from '../types.ts';
import { ev } from './combat.ts';
import { titanShake } from './neck.ts';

/**
 * The bell at midnight (El Párpado 4, docs/SAGAS.md): it has to ring with the titan's pulse. A ring of light closes on
 * it again and again, as on Las vías' Anvil, a little quicker after every blow on the beat. A hero by the bell who
 * strikes it (the hammer command) as the ring closes pushes every nightmare round the post back, stuns them a moment,
 * and the calm goes up one. Off the beat the hammer is up a moment and the eyelid trembles: the titan shakes, and
 * whoever is walking falls. The calm full wins it; the nightmares cutting the bell's rope (the node) loses it.
 */

/** How long a ring takes to close after `n` blows on the beat. */
const ringOf = (s: BattleState, n: number): number => {
  const r = s.rules?.toll;
  return r ? imax(r.ring[1], r.ring[0] - n * r.step) : 0;
};

export const tollTick = (s: BattleState): void => {
  const r = s.rules?.toll;
  if (!r) return;
  const st = (s.toll ??= { open: r.first - r.ring[0], close: r.first, calm: 0, rung: -1, done: 0, up: {} });
  if (!s.started || st.done) return;
  // Closed and gone by unstruck: the next ring, as quick as that one.
  if (s.waveT > st.close + r.window) {
    if (st.rung !== st.close) ev(s, { t: 'toll', kind: 'lapse', n: st.calm, x: 0, y: 0 });
    st.open = s.waveT;
    st.close = s.waveT + ringOf(s, st.calm);
  }
};

/** The hammer command at the bell: on the beat or not. */
export const tollStrike = (s: BattleState, h: Hero): void => {
  const r = s.rules?.toll;
  const st = s.toll;
  if (!r || !st || !s.started || st.done || h.downTicks > 0) return;
  if ((st.up[h.pid] ?? 0) > s.tick || !withinRange(h.x, h.y, 0, 0, r.reach)) return;
  h.facing = iatan2(-h.y, -h.x);
  if (st.rung === st.close || s.waveT < st.close - r.window || s.waveT > st.close + r.window) {
    // Off the beat: the eyelid trembles.
    st.up[h.pid] = s.tick + r.miss;
    ev(s, { t: 'toll', kind: 'miss', n: st.calm, x: h.x, y: h.y });
    titanShake(s);
    return;
  }
  st.rung = st.close;
  st.calm = imin(r.need, st.calm + 1);
  // The sound goes through the head: every nightmare round the post is pushed back from the bell, and stops a moment.
  for (const e of s.enemies) {
    if (e.hp <= 0 || e.kind === 'boneWall' || !withinRange(e.x, e.y, 0, 0, r.radius)) continue;
    const d = imax(1, isqrt(e.x * e.x + e.y * e.y));
    e.x += idiv(e.x * r.push, d);
    e.y += idiv(e.y * r.push, d);
    e.stun = imax(e.stun, r.stun);
  }
  if (st.calm >= r.need) {
    st.done = s.tick;
    ev(s, { t: 'toll', kind: 'calm', n: st.calm, x: 0, y: 0 });
    return;
  }
  ev(s, { t: 'toll', kind: 'hit', n: st.calm, x: 0, y: 0 });
  // The next ring closes from now, a little quicker.
  st.open = s.waveT;
  st.close = s.waveT + ringOf(s, st.calm);
};

/** The calm full: won (a moment later); the rope cut: lost. */
export const tollResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.toll;
  if (!r) return null;
  if (s.keep.hp <= 0) return 'lost';
  return s.toll?.done && s.tick - s.toll.done >= r.beat ? 'won' : null;
};
